import { useEffect, useMemo, useState } from "react";
import config from "./config";
import { cachedPost } from "./apiCache";
import { applyCommunityTheme } from "./applyFont";
import {
  applyThemeMode,
  resolveThemeMode,
  subscribeThemeMode,
  themeSwitchEnabled,
} from "./theme";
import { paletteForMode } from "./brandColor";

// Single source of truth for the community's brand palette — the SAME call the
// login page uses (native getfunnel → funnel_membership_settings.brand_settings).
// Every post-login surface (header, section cards, buttons) themes off this so
// the colours are guaranteed identical to the login page, independent of the
// membership-groups `community` payload.
//
// Returns { primary, secondary, btnText, brand, themeMode, themeSwitch } and, as
// a side effect, publishes --brand-secondary / --brand-btn-text CSS variables
// (see applyCommunityTheme) and paints the light/dark mode on <html> (see
// theme.js). Mode rides on brand_settings for the same reason the palette does:
// it is the owner's branding decision, and this is the one call every surface
// already makes.
export function useBrand(token) {
  // Seed from the last-known palette for this membership (cached on a prior visit)
  // so a page refresh themes instantly — no flash of the default blue while the
  // network call is in flight.
  const [brand, setBrand] = useState(() => {
    if (!token) return null;
    try {
      const cached = localStorage.getItem(`brand:${token}`);
      return cached ? JSON.parse(cached) : null;
    } catch {
      return null;
    }
  });

  // The palette depends on the mode (see paletteForMode), and a member flipping
  // the header switch changes the mode without touching `brand` — so subscribe,
  // or every consumer keeps rendering the palette for the mode it mounted in.
  // Membership feature flags that ride on the same getfunnel call. Cached
  // alongside the palette so a reload knows immediately whether to gate —
  // otherwise the shell would fire get-myplans once before the answer landed,
  // which is the request this flag exists to avoid.
  const [features, setFeatures] = useState(() => {
    if (!token) return null;
    try {
      const cached = localStorage.getItem(`features:${token}`);
      return cached ? JSON.parse(cached) : null;
    } catch {
      return null;
    }
  });

  const [mode, setMode] = useState(() =>
    typeof document !== "undefined"
      ? document.documentElement.getAttribute("data-theme") || "light"
      : "light"
  );
  useEffect(() => subscribeThemeMode(setMode), []);

  // Apply the cached theme's CSS variables immediately on mount (before fetch).
  // The light/dark mode is painted here too — the inline boot script in
  // index.html has already set it from its own cache, so this only corrects a
  // membership whose cached brand is newer than that.
  useEffect(() => {
    if (brand) {
      applyCommunityTheme(brand);
      setMode(applyThemeMode(resolveThemeMode(brand, token), token));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    // Brand settings don't change within a session and every post-login surface
    // calls this — cache long so 7+ components share one network call.
    // base_url disambiguates the slug: membership_code is unique per DOMAIN, not
    // globally, so two customers can both own "web". Without it the API resolves
    // to whichever funnel has the lower id and this membership gets another
    // customer's branding. Sent only from this app — the older React client on
    // the same endpoint is left alone and keeps its previous behaviour.
    cachedPost(
      `${config.API_URL}/getfunnel`,
      { funnel_id: token, base_url: window.location.host },
      { ttl: 300000 }
    )
      .then((res) => {
        if (cancelled) return;
        const fd = res.data?.funnel_data?.[0];
        let b = {};
        try {
          b = fd?.brand_settings
            ? typeof fd.brand_settings === "string"
              ? JSON.parse(fd.brand_settings)
              : fd.brand_settings
            : {};
        } catch {
          b = {};
        }
        setBrand(b);
        applyCommunityTheme(b);
        setMode(applyThemeMode(resolveThemeMode(b, token), token));
        try { localStorage.setItem(`brand:${token}`, JSON.stringify(b)); } catch { /* ignore */ }

        const f = res.data?.features || {};
        setFeatures(f);
        try { localStorage.setItem(`features:${token}`, JSON.stringify(f)); } catch { /* ignore */ }
      })
      .catch(() => {
        /* keep defaults on failure */
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  return useMemo(() => {
    const b = brand || {};
    // Mode-corrected, not raw: a near-black theme_color1 (the stock #012329, or
    // the Notion Ink preset) is invisible on the dark surface, so it is lifted
    // to stay readable. A palette that already works in dark mode comes back
    // untouched, and light mode is always the creator's literal choice.
    const palette = paletteForMode(b, mode);
    return {
      brand: b,
      primary: palette.primary,
      secondary: palette.secondary,
      btnText: palette.btnText,
      // The creator's literal colour, for the rare surface that must match it
      // exactly (a logo lockup) rather than merely stay legible.
      rawPrimary: b.theme_color1 || "#0274F7",
      // What the app is currently rendering, and whether the member is allowed
      // to change it — the header's theme control reads both.
      themeMode: mode,
      themeSwitch: themeSwitchEnabled(b),
      /*
       * Creator's "Subscription Expiry Popup" toggle (admin → Settings →
       * Features). Default OFF, and null while the first call is still in
       * flight — the shell treats anything but `true` as off, so a membership
       * that has not opted in never even asks for the member's plans.
       */
      subscriptionGate: features?.subscription_gate === true,
    };
  }, [brand, mode, features]);
}
